import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { authedFetch } from '../../utils/authedFetch';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export default function PatientDemosForm({
  data,
  onChange,
  loadingPatient = false,
  flow = 'scenario-create', // 'patient-create' | 'patient-edit' | 'scenario-create'
}) {
  const API_BASE = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { user, session, authLoading } = useAuth();

  const isPatientCreate = flow === 'patient-create';
  const isPatientEdit = flow === 'patient-edit';
  const isScenarioCreate = flow === 'scenario-create';
  const demographicsDisabled = isPatientEdit;

  const [patient, setPatient] = useState({
    name: '',
    dob: null,
    sex: '',
    ...data?.patient,
  });

  const [allPatients, setAllPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);

  useEffect(() => {
    setPatient({
      name: '',
      dob: null,
      sex: '',
      ...data?.patient,
    });
  }, [data?.patient]);

  useEffect(() => {
    if (!isScenarioCreate) return;
    if (authLoading || !session || !user) return;

    const loadPatients = async () => {
      setLoadingPatients(true);

      try {
        const res = await authedFetch({
          accessToken: session.access_token,
          apiBase: API_BASE,
          path: '/api/getpatients',
          navigate,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const patients = await res.json();
        setAllPatients(Array.isArray(patients) ? patients : []);
      } catch (err) {
        console.error('Failed to load patients:', err);
        setAllPatients([]);
      } finally {
        setLoadingPatients(false);
      }
  };
      loadPatients();
  }, [API_BASE, authLoading, isScenarioCreate, navigate, session, user]);

  const handleGetExistingData = async (selected) => {
    if (!selected || !session?.access_token) return;

    setIsLoadingPatientData(true);

    try {
      const res = await authedFetch({
        accessToken: session.access_token,
        apiBase: API_BASE,
        path: `/api/full-patient/${selected.id}`,
        navigate,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const fullData = await res.json();

      onChange(
        {
          patient: {
            id: fullData.patient.id,
            name: fullData.patient.name,
            dob: fullData.patient.dob,
            sex: fullData.patient.sex,
          },
          medicalhx: fullData.medicalhx,
          surghx: fullData.surghx,
          eprDocs: fullData.eprDocs,
          eprBloods: JSON.parse(JSON.stringify(fullData.eprBloods)),
          eprObs: fullData.eprObs,
          medications: fullData.medications,
          allergies: fullData.allergies,
          alerts: fullData.alerts,
        },
        {
          markSaved: true,
          markOtherStepsSaved: true,
        }
      );

      setPatient({
        id: fullData.patient.id,
        name: fullData.patient.name,
        dob: fullData.patient.dob,
        sex: fullData.patient.sex,
      });
    } catch (err) {
      console.error('Error loading full patient data:', err);
      alert('Failed to load full patient record.');
    } finally {
      setIsLoadingPatientData(false);
    }
  };

  const handlePatientChange = (field) => (e) => {
    const updated = { ...patient, [field]: e.target.value };
    setPatient(updated);
    onChange({ patient: updated });
  };

  const handleDOBChange = (newDate) => {
    const updated = { ...patient, dob: newDate };
    setPatient(updated);
    onChange({ patient: updated });
  };

  const renderDemographicsForm = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        width: '100%',
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {isPatientEdit ? 'Patient demographics' : 'Create new patient'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isPatientEdit
              ? 'This patient record is being viewed in edit mode. Demographics are locked here.'
              : 'Enter the core demographics for the patient record.'}
          </Typography>
        </Box>

        <TextField
          label="Patient name"
          value={patient.name || ''}
          disabled={demographicsDisabled}
          onChange={handlePatientChange('name')}
          fullWidth
          required
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr' },
            gap: 2,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date of birth"
              disabled={demographicsDisabled}
              value={patient.dob ? dayjs(patient.dob) : null}
              onChange={handleDOBChange}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </LocalizationProvider>

          <FormControl fullWidth required>
            <InputLabel>Sex</InputLabel>
            <Select
              value={patient.sex || ''}
              disabled={demographicsDisabled}
              onChange={handlePatientChange('sex')}
              label="Sex"
            >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {!isPatientEdit && (
          <Typography variant="caption" color="text.secondary">
            Patient names must be unique in the system.
          </Typography>
        )}
      </Stack>
    </Paper>
  );

  return (
    <>
      <Box
        sx={{
          width: '100%',
          maxWidth: 1100,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: 2,
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {isPatientCreate && 'Create patient'}
              {isPatientEdit && 'Edit patient'}
              {isScenarioCreate && 'Patient details'}
            </Typography>

            <Typography variant="body1" color="text.secondary">
              {isPatientCreate &&
                'Create a new patient record by entering the core demographics below.'}
              {isPatientEdit &&
                'This patient already exists in the system. Demographics are shown here for reference.'}
              {isScenarioCreate &&
                'Before adding scenario details, either load an existing patient record or create a new patient.'}
            </Typography>
          </Box>

          {isPatientEdit && (
            <Alert severity="warning" sx={{ borderRadius: 2, width:'75%' }}>
              You opened an existing patient record. Demographics are locked on this screen.
            </Alert>
          )}

          {isScenarioCreate && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Loading an existing patient will also load their linked EPR data. Creating a new
              patient will start a fresh record for this scenario flow.
            </Alert>
          )}

          {isScenarioCreate ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                gap: 3,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  width: '100%',
                }}
              >
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Load existing patient
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select a saved patient to load their demographics and linked EPR data.
                    </Typography>
                  </Box>
                  <Box sx={{width:'90%'}}>
                  <Autocomplete
                    options={allPatients}
                    loading={loadingPatients}
                    value={selectedPatient}
                    onChange={(e, value) => setSelectedPatient(value)}
                    getOptionLabel={(option) => {
                      const dob = option?.dob
                        ? dayjs(option.dob).format('DD/MM/YYYY')
                        : 'Unknown DOB';
                      return `${option.name} (DoB ${dob})`;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search existing patients"
                        fullWidth
                      />
                    )}
                  /></Box>

                  <Button
                    variant="contained"
                    disabled={!selectedPatient}
                    onClick={() => handleGetExistingData(selectedPatient)}
                    sx={{ alignSelf: 'flex-start', minWidth: 180 }}
                  >
                    Load patient
                  </Button>
                </Stack>
              </Paper>

              {renderDemographicsForm()}
            </Box>
          ) : (
            <Box sx={{ width: '100%', maxWidth: 820 }}>
              {renderDemographicsForm()}
            </Box>
          )}
        </Stack>
      </Box>

      <Modal open={isLoadingPatientData || loadingPatient}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            bgcolor: 'rgba(255,255,255,0.55)',
            px: 2,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              px: 4,
              py: 3,
              borderRadius: 3,
              minWidth: 260,
            }}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress />
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 600 }}>
                  Loading patient data...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Preparing demographics and linked records
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Modal>
    </>
  );
}